import React from "react";
import { Menu as MenuIcon, Close as CloseIcon } from "@mui/icons-material";

import type { NavBarProps } from "./NavBar.types";
import { defaultItems, defaultLogo, defaultActiveHref } from "./NavBarDefaults";
import {
  GlassAppBar,
  TransparentAppBar,
  NavToolbar,
  LogoBox,
  LogoImage,
  UserActionsBox,
  MenuBox,
  DrawerMenuBox,
  MenuItem,
  MobileMenuButton,
  MobileDrawer,
} from "./NavBar.styles";

/**
 * NavBar Component
 * 
 * A responsive navigation bar component with support for:
 * - Desktop and mobile views
 * - Transparent and glassmorphism styles
 * - Optional Clerk authentication integration
 * - Customizable navigation items and logo
 */
const NavBar: React.FC<NavBarProps> = ({
  items = defaultItems,
  logo = defaultLogo,
  activeHref = defaultActiveHref,
  mobileOpen = false,
  isMobile = false,
  onToggleMobileMenu,
  clerkUserButton,
  clerkUserMenu,
  renderUserActions,
}) => {
  const AppBarComponent = isMobile ? GlassAppBar : TransparentAppBar;

  /**
   * Determines which user action component to render based on priority:
   * 1. renderUserActions (highest priority - recommended for Clerk)
   * 2. clerkUserButton
   * 3. clerkUserMenu
   */
  const renderUserSection = (): React.ReactNode => {
    if (renderUserActions) {
      return renderUserActions();
    }
    if (clerkUserButton) {
      return clerkUserButton;
    }
    if (clerkUserMenu) {
      return clerkUserMenu;
    }
    return null;
  };

  const userSection = renderUserSection();

  return (
    <AppBarComponent>
      <NavToolbar>
        {/* Logo */}
        {logo?.logoSrc && (
          <LogoBox>
            <a href={logo.logoHref}>
              <LogoImage>
                <img src={logo.logoSrc} alt={logo.logoAlt || "Logo"} />
              </LogoImage>
            </a>
          </LogoBox>
        )}

        {/* Desktop Menu */}
        {!isMobile && (
          <MenuBox>
            {items.map((item) => (
              <MenuItem
                key={item.href}
                active={item.href === activeHref}
                component="a"
                href={item.href}
              >
                {item.label}
              </MenuItem>
            ))}
            {userSection && <UserActionsBox>{userSection}</UserActionsBox>}
          </MenuBox>
        )}

        {/* Mobile Menu */}
        {isMobile && (
          <>
            <MenuBox>
              {userSection && <UserActionsBox>{userSection}</UserActionsBox>}
              <MobileMenuButton onClick={onToggleMobileMenu}>
                {mobileOpen ? <CloseIcon /> : <MenuIcon />}
              </MobileMenuButton>
            </MenuBox>
            <MobileDrawer anchor="right" open={mobileOpen} onClose={onToggleMobileMenu}>
              <DrawerMenuBox>
                {items.map((item) => (
                  <MenuItem
                    key={item.href}
                    active={item.href === activeHref}
                    component="a"
                    href={item.href}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </DrawerMenuBox>
            </MobileDrawer>
          </>
        )}
      </NavToolbar>
    </AppBarComponent>
  );
};

export { NavBar };

const NavBarComponent = NavBar as React.FC<NavBarProps>;
export default NavBarComponent;